import React, { useEffect, useState } from "react";
import { useI18n } from "../i18n";
import { getCountries, getCountry, api } from "../lib/api";
import { MultiLineChart, COLORS } from "../components/Charts";
import { Car } from "lucide-react";

const REGIONS = ["All", "North America", "South America", "Europe", "Asia", "Oceania"];

export default function Geography() {
  const { t, lang } = useI18n();
  const [countries, setCountries] = useState([]);
  const [region, setRegion] = useState("All");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    getCountries().then((d) => setCountries(d.countries || []));
  }, []);

  const filtered = countries.filter((c) => region === "All" || c.region === region);

  const openCountry = async (c) => {
    const d = await getCountry(c.code);
    setSelected(d);
  };

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-28 pb-16" data-testid="page-geography">
      <div>
        <span className="text-xs uppercase tracking-[0.2em] text-violet-400 font-mono">
          {lang === "pt" ? "Mapa de oportunidades" : "Opportunity map"}
        </span>
        <h1 className="mt-2 font-display text-4xl lg:text-5xl tracking-tight">{t.geography.title}</h1>
        <p className="mt-3 max-w-2xl text-neutral-400">{t.geography.sub}</p>
      </div>

      <div className="mt-8 flex flex-wrap gap-2" data-testid="region-filter">
        {REGIONS.map((r) => (
          <button
            key={r}
            onClick={() => setRegion(r)}
            className={`px-3 py-1.5 rounded-md border text-xs font-mono uppercase tracking-[0.15em] transition-colors ${
              region === r
                ? "bg-gold-500 border-gold-500 text-black"
                : "bg-white/5 border-white/10 text-neutral-400 hover:text-white"
            }`}
            data-testid={`region-${r.replace(/\s+/g, "-").toLowerCase()}`}
          >
            {r === "All" ? t.common.all : r}
          </button>
        ))}
      </div>

      <div className="mt-8 grid lg:grid-cols-12 gap-6">
        <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4" data-testid="country-grid">
          {filtered.map((c) => (
            <button
              key={c.code}
              onClick={() => openCountry(c)}
              className={`text-left surface p-5 hover:border-white/25 transition-colors ${
                selected?.code === c.code ? "border-gold-500/50 ring-1 ring-gold-500/30" : ""
              }`}
              data-testid={`country-card-${c.code}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-3xl">{c.flag}</span>
                  <div>
                    <div className="font-display text-lg">{lang === "pt" ? c.name_pt : c.name_en}</div>
                    <div className="text-xs text-neutral-500 font-mono uppercase tracking-widest">
                      {c.region} · {c.currency}
                    </div>
                  </div>
                </div>
                <span
                  className={`text-xs px-2 py-0.5 rounded-full font-mono ${
                    c.risk_score < 25
                      ? "bg-gold-500/15 text-gold-400 border border-gold-500/20"
                      : c.risk_score < 45
                      ? "bg-gold-500/15 text-gold-300 border border-gold-500/20"
                      : "bg-red-500/15 text-red-400 border border-red-500/20"
                  }`}
                >
                  {lang === "pt" ? "Risco" : "Risk"} {c.risk_score}
                </span>
              </div>
              <div className="mt-4 grid grid-cols-3 gap-3 text-xs font-mono">
                <Stat label={t.geography.growth} value={`${c.gdp_growth_pct}%`} good={c.gdp_growth_pct > 2} />
                <Stat label={t.geography.real_estate} value={`${c.real_estate_cagr_pct}%`} good={c.real_estate_cagr_pct > 5} />
                <Stat label={t.geography.stocks} value={`${c.stock_market_10y_return_pct}%`} good={c.stock_market_10y_return_pct > 8} />
              </div>
            </button>
          ))}
        </div>

        <div className="lg:col-span-5">
          <div className="surface p-5 sticky top-28" data-testid="country-detail">
            {!selected ? (
              <>
                <h3 className="font-display text-xl">
                  {lang === "pt" ? "Selecione um país" : "Pick a country"}
                </h3>
                <p className="text-sm text-neutral-500 mt-2">
                  {lang === "pt"
                    ? "Você verá projeção de bolsa e imóveis em 10 anos."
                    : "You'll see the 10-year stock and real estate projection."}
                </p>
              </>
            ) : (
              <>
                <div className="flex items-center gap-3">
                  <span className="text-4xl">{selected.flag}</span>
                  <div>
                    <div className="text-xs uppercase tracking-[0.2em] text-neutral-500 font-mono">
                      {selected.region} · {selected.currency}
                    </div>
                    <h3 className="font-display text-2xl">
                      {lang === "pt" ? selected.name_pt : selected.name_en}
                    </h3>
                  </div>
                </div>

                <div className="mt-4 grid grid-cols-2 gap-3 text-sm font-mono">
                  <KV label={t.geography.growth} value={`${selected.gdp_growth_pct}%`} />
                  <KV label={t.geography.inflation} value={`${selected.inflation_pct}%`} />
                  <KV label={t.geography.real_estate} value={`${selected.real_estate_cagr_pct}%`} />
                  <KV label={t.geography.stocks} value={`${selected.stock_market_10y_return_pct}%`} />
                  <KV label={t.geography.yield} value={`${selected.rent_yield_pct}%`} />
                  <KV
                    label={t.geography.climate}
                    value={lang === "pt" ? selected.business_climate : selected.business_climate_en}
                  />
                </div>

                <div className="mt-5">
                  <MultiLineChart
                    series={[
                      {
                        label: lang === "pt" ? "Bolsa" : "Stocks",
                        data: selected.stock_projection_100_10y.map((p) => ({ year: p.year, value: p.value })),
                        color: COLORS.emerald,
                      },
                      {
                        label: lang === "pt" ? "Imóveis" : "Real Estate",
                        data: selected.real_estate_projection_100_10y.map((p) => ({ year: p.year, value: p.value })),
                        color: COLORS.gold,
                      },
                    ]}
                    years={10}
                    height={200}
                    testid="country-chart"
                  />
                </div>

                <div className="mt-4 border-t border-white/5 pt-4">
                  <div className="text-xs uppercase tracking-[0.2em] text-gold-400 font-mono">
                    {lang === "pt" ? "Destaques" : "Highlights"}
                  </div>
                  <ul className="mt-2 space-y-1.5">
                    {(lang === "pt" ? selected.highlights_pt : selected.highlights_en).map((h, i) => (
                      <li key={i} className="text-sm text-neutral-300 flex gap-2">
                        <span className="text-gold-400 font-mono">›</span>
                        {h}
                      </li>
                    ))}
                  </ul>
                </div>

                {selected.cities?.length > 0 && (
                  <div className="mt-5 border-t border-white/5 pt-4">
                    <div className="text-xs uppercase tracking-[0.2em] text-neutral-500 font-mono">
                      {t.geography.cities}
                    </div>
                    <div className="mt-2 space-y-2">
                      {selected.cities.map((c) => (
                        <div
                          key={c.city}
                          className="flex items-center justify-between text-xs font-mono border-b border-white/5 pb-1.5"
                        >
                          <span className="text-white">
                            {c.city}
                            {typeof c.population_m === "number" && (
                              <span className="text-neutral-500 ml-2">
                                · {c.population_m}M {t.geography.population.toLowerCase()}
                              </span>
                            )}
                          </span>
                          <span className="text-neutral-400">
                            ${c.avg_price_m2_usd.toLocaleString()}/m² ·{" "}
                            <span className={c.growth_5y_pct >= 0 ? "text-gold-400" : "text-red-400"}>
                              {c.growth_5y_pct >= 0 ? "+" : ""}
                              {c.growth_5y_pct}% 5y
                            </span>
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <CarBlock code={selected.code} lang={lang} label={t.geography.car} />
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const Stat = ({ label, value, good }) => (
  <div>
    <div className="text-[10px] text-neutral-500 uppercase tracking-widest">{label}</div>
    <div className={`mt-0.5 ${good ? "text-gold-400" : "text-white"}`}>{value}</div>
  </div>
);

const KV = ({ label, value }) => (
  <div className="surface-elevated p-2.5 rounded-md">
    <div className="text-[10px] text-neutral-500 uppercase tracking-widest">{label}</div>
    <div className="mt-0.5 text-white">{value}</div>
  </div>
);

function CarBlock({ code, lang, label }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!code) return;
    api.get(`/cars?country=${code}&lang=${lang}`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [code, lang]);
  if (!data) return null;
  const pct = data.five_year_loss_pct;
  return (
    <div className="mt-5 border-t border-white/5 pt-4" data-testid="car-block">
      <div className="text-xs uppercase tracking-[0.2em] text-neutral-500 font-mono flex items-center gap-1.5">
        <Car size={11} /> {label}
      </div>
      <div className="mt-2 flex items-baseline gap-3">
        <span className="font-mono text-3xl text-red-400">-{pct}%</span>
        <span className="text-xs text-neutral-500 font-mono">
          {lang === "pt" ? "em 5 anos" : "over 5 years"}
        </span>
      </div>
      <p className="mt-2 text-xs text-neutral-400 leading-relaxed">
        {lang === "pt" ? data.note_pt : data.note_en}
      </p>
    </div>
  );
}
